import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRouter as createTanStackRouter } from "@tanstack/react-router";
import { createTRPCClient, httpBatchLink } from "@trpc/client";
import { createTRPCOptionsProxy } from "@trpc/tanstack-react-query";

import type { AppRouter } from "~backend/trpc/router";
import { auth } from "~frontend/lib/auth";
import { routeTree } from "~frontend/routeTree.gen";

export const queryClient = new QueryClient();

export const trpc = createTRPCOptionsProxy<AppRouter>({
  client: createTRPCClient({
    links: [
      httpBatchLink({
        url: `${process.env.VITE_BACKEND_URL}/trpc`,
      }),
    ],
  }),
  queryClient,
});

export function createRouter() {
  const router = createTanStackRouter({
    routeTree,
    scrollRestoration: true,
    defaultPreload: "intent",
    // Provide a strongly-typed router context so that `context` inside
    // route loaders/components is properly inferred.
    context: {
      trpc,
      queryClient,
      auth,
    } as const,
    defaultPendingComponent: () => (
      <div className={`p-2 text-2xl`}>Loading</div>
    ),
    Wrap: function WrapComponent({ children }) {
      return (
        <QueryClientProvider client={queryClient}>
          {children}
        </QueryClientProvider>
      );
    },
  });

  return router;
}

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof createRouter>;
  }
}

// Extend RouterContext so it is available in loaders/components
// This ensures `context.trpc`, `context.queryClient`, and `context.auth` are
// strongly typed wherever the TanStack Router context is available.
declare module "@tanstack/react-router" {
  interface RouterContext {
    trpc: typeof trpc;
    queryClient: QueryClient;
    auth: typeof auth;
  }
}

